import { Form, Input, Select } from 'antd';
import DollarInputPayment from 'components/DollarAmount/DollarInputPayment';
import { FormItem } from 'components/Form';
import Modal from 'components/Modal';
import { EPeriodDates } from 'features/cashier/components/CenterMainBody/ExtendFeatures/GiftCard/IssueGiftCard';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import styled from 'styled-components';
import CustomerSearchInput, { unknownCustomerId } from '../ModalAddGiftCard/CustomerSearchInput';
import InputGiftCard from '../ModalAddGiftCard/InputGiftCard';
import { ICustomerItemData } from 'services/shop/types/customer';
import giftCardsApis from 'features/giftCards/services/apis';
import { IAddManUalGiftCardBody, IUpdateGiftCardBody } from 'features/giftCards/services/types/api';
import moment from 'moment';
import { useSetLoadingPage } from 'services/UI/LoadingPage';
import { IResponseDataBody } from 'services/response';
import giftCardsActions from 'features/giftCards/services/actions';
import { useAppDispatch } from 'store/hooks';
import Text from 'components/Text';
import radio_active_icon from 'features/cashier/components/CashDeposit/icons/select-box-active.svg';
import radio_icon from 'features/cashier/components/CashDeposit/icons/select-box.svg';

const FormStyled = styled(Form)`
  .ant-form-item .ant-form-item-label label {
    color: #1D2129;
    font-family: Poppins;
    font-size: 20px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }
  .dollar-input-selector {
    height: 3.5rem;
    padding: 0px 11px;
    .dollar-input-text {
      color: #1D2129!important;
      font-family: Poppins;
      font-size: 22px;
      height: unset;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
    }
  }
  .ant-input.note-input, .ant-select.expireDate .ant-select-selector .ant-select-selection-item {
    color: #1D2129;
    font-family: Poppins;
    font-size: 22px;
    font-style: normal;
    font-weight: 500;
  }

  .balance {
    .ant-form-item-label > label {
      color: red !important;
    }

    .dollar-input-text, .text-measure {
      color: red !important;
    }
    .dollar-input-selector {
      border-color: red;
    }

    .text-placeholder {
      color: red !important;
    }

    input {
      z-index: 0;
    }
  }
`;

const RowAmount = styled.div`
display: flex;
gap: 16px;
align-self: stretch;
`;

const periodDates = [
  { label: '1 month', value: EPeriodDates.MONTH_1, },
  { label: '3 month', value: EPeriodDates.MONTH_3 },
  { label: '6 month', value: EPeriodDates.MONTH_6 },
  { label: '12 month', value: EPeriodDates.MONTH_12 },
  { label: 'No limit', value: EPeriodDates.NO_LIMIT },
];


interface IFormValues {
    amount: number;
    usedAmount: number;
    balance: number;
    note: string;
    expireDate: number;
    status?: boolean;
}

interface IDetailGiftCard {
    usedAmount: number;
    amount: number;
    balance: number;
    note: string;
    expireDate: string;
    giftCardId: string;
    updatedBy: string | null;
    createdDate: string;
    status?: boolean;
}

type Props = {};
type Ref = {
  open: (data: IDetailGiftCard) => void;
};

const getPeriodValue = (createdDate: string, expireDate: string) => {
  const months = Math.ceil(moment(expireDate).diff(moment(createdDate), 'months', true));

  if (months === 1) return EPeriodDates.MONTH_1;
  if (months === 3) return EPeriodDates.MONTH_3;
  if (months === 6) return EPeriodDates.MONTH_6;
  if (months === 12) return EPeriodDates.MONTH_12;

  return EPeriodDates.NO_LIMIT;
};


const PAGE_SIZE = 20;
export const useModalEditGiftCardRef = () => useRef<Ref>(null);
const ModalEditGiftCard = forwardRef<Ref, Props>(({ }, ref) => {
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const setLoadingPage = useSetLoadingPage();
  const dispatch = useAppDispatch();
  const [detailGiftCard, setDetailGiftCard] = useState<IDetailGiftCard | null>(null);
  const onClose = () => {
    setOpen(false);
    form.resetFields();
  };

  const onOpen: Ref['open'] = (data) => {
    setDetailGiftCard(data);

    form.setFieldsValue({
      amount: data.amount,
      usedAmount: data.usedAmount,
      note: data.note,
      expireDate: getPeriodValue(data.createdDate, data.expireDate),
      balance: data.balance,
      status: data.status,
    });

    setOpen(true);
  };

  const amount = Form.useWatch('amount', form);
  const usedAmount = Form.useWatch('usedAmount', form);

  useEffect(() => {
    form.setFieldValue(
      'balance',
      (amount || 0) - (usedAmount || 0)
    );
  }, [amount, usedAmount]);

  const handleFinish = async (values: IFormValues) => {
    const body: IUpdateGiftCardBody = {
      giftCardId: detailGiftCard?.giftCardId || '',
      amount: values.amount,
      expireDate: moment(values.expireDate).format('MM-DD-YYYY'),
      usedAmount: values.usedAmount,
      balance: values.balance,
      note: values.note,
      updatedBy: detailGiftCard?.updatedBy || null,  
      status: values.status,
    };
    setLoadingPage(true);
    const nowStr = moment().format('MM-DD-YYYY');
    try {
      const res: IResponseDataBody<any> = await giftCardsApis.updateGiftCard(body);
      if (res.data.data) {
        const payload = {
          page: 1,
          size: PAGE_SIZE,
          startDate: nowStr,
          endDate: nowStr,
        };
        dispatch(giftCardsActions.getListGiftCard.fetch(payload));
        onClose();
      }
    } catch (error) {
    } finally {
      setLoadingPage(false);
    }
  };

  useImperativeHandle(ref, () => ({
    open: onOpen
  }));

  return (
    <Modal
      visible={open}
      modalTitle='EDIT GIFT CARD'
      onClose={onClose}
      okTitle='Ok'
      onSubmit={() => form.submit()}
      containerPadding={1}
      noneBodyStyle
      noneFooterStyle
      v2
      className={'modal-form-input-check modal-non-opacity modal-bg-v2'}
      width={'80vw'}
    >
      <FormStyled<any> form={form} onFinish={handleFinish} layout='vertical' initialValues={{ expireDate: EPeriodDates.MONTH_12 }}>
        <RowAmount>
          <FormItem label='Amount' name={'amount'} rules={[{ required: true }]} style={{ flex: 1 }}>
            <DollarInputPayment />
          </FormItem>
          <FormItem label='Used Amount' name={'usedAmount'} rules={[{ required: true }]} style={{ flex: 1 }}>
            <DollarInputPayment />
          </FormItem>
        </RowAmount>
        <RowAmount>
            <FormItem label='Balance' name={'balance'} rules={[{ required: true }]} style={{ flex: 1 }} className='balance'>
                <DollarInputPayment disabled/>
            </FormItem>
            <FormItem label={'Expiration'} name={'expireDate'} rules={[{ required: true }]} style={{ flex: 1 }}>
            <Select className='expireDate' options={periodDates} />
            </FormItem>
        </RowAmount>
        <Form.Item label='Status' name={'status'}>
          <GiftCardStatusOption />
        </Form.Item>
        <FormItem label='Note' name={'note'} >
          <Input.TextArea className='note-input' rows={4} placeholder='Please enter' />
        </FormItem>
      </FormStyled>
    </Modal>
  );
});
ModalEditGiftCard.displayName = 'ModalEditGiftCard';
export default ModalEditGiftCard;

type GiftCardStatusOptionProps = {
  value?: boolean;
  onChange?: (value: boolean) => void;
};

const OPTIONS = [
  { value: true, label: 'Activated' },
  { value: false, label: 'Inactivate'},
];

const GiftCardStatusOption = ({ value, onChange }: GiftCardStatusOptionProps) => {
  return (
    <GiftCardStatusContainer>
      {OPTIONS.map((opt, index) => (
        <OptionItemContainer key={index} type='button' onClick={() => onChange?.(opt.value)}>
          <img
            src={value === opt.value ? radio_active_icon : radio_icon}
            alt='radio'
            style={{ width: 40, height: 40 }}
          />
          <Text variant='H5' className='label'>
            {opt.label}
          </Text>
        </OptionItemContainer>
      ))}
    </GiftCardStatusContainer>
  );
};

const GiftCardStatusContainer = styled.div`
  display: flex;
  width: fit-content;
  gap: 40px;
`;

const OptionItemContainer = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  align-self: stretch;
  border: none;
  background: none;
  cursor: pointer;

  .label {
    font-family: Poppins;
    font-weight: 400;
  }
`;
